import React, {useState, useEffect} from 'react';
import {
  AssistantRuntimeProvider,
  CompositeAttachmentAdapter,
  SimpleImageAttachmentAdapter,
  useLocalRuntime
} from '@assistant-ui/react';
import {auth} from '../../helpers/firebase';
import Loading from '../Loading';
import {ThreadListProvider} from '../../contexts/threadListContext';
import {processAttachment} from '../../utils/imageUtils';

// Create an image attachment adapter with proper configuration
const imageAdapter = new SimpleImageAttachmentAdapter({
  accept: 'image/*',
  maxSize: 5 * 1024 * 1024, // 5MB
  async add({file, url}) {
    console.log('Adding image:', {file, url});
    return {
      type: 'image',
      url: url || URL.createObjectURL(file),
      file
    };
  }
});

const attachmentAdapter = new CompositeAttachmentAdapter([imageAdapter]);

/**
 * @link https://www.assistant-ui.com/docs/runtimes/custom-rest
 *
 * @type {{run({messages: *, abortSignal: *}): Promise<{content: [{type: string, text: *}]}>}}
 */
const MyModelAdapter = {
  async *run({messages, abortSignal}) {
    try {
      const appLabel = localStorage.getItem('avadaSupportAiAgentApp') || 'app_joy';
      const channel = localStorage.getItem('avadaSupportAiAgentChannel') || 'crisp';
      const threadId = localStorage.getItem('avadaSupportAiAgentThreadId') || '';
      const idToken = await auth.currentUser.getIdToken(false);

      // Process messages and convert attachments to base64
      const processedMessages = await Promise.all(
        messages.map(async msg => {
          const processedContent = Array.isArray(msg.content) ? msg.content : [msg.content];
          const attachments = msg.attachments || [];

          // Process attachments using utility function
          const processedAttachments = await Promise.all(
            attachments.map(processAttachment)
          );

          return {
            role: msg.role,
            content: processedContent,
            attachments: processedAttachments
          };
        })
      );

      const response = await fetch(`${process.env.REACT_APP_API_URL_FUNC}/api/thread/${threadId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': idToken,
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
          'Connection': 'keep-alive'
        },
        body: JSON.stringify({
          messages: processedMessages,
          appLabel,
          channel
        }),
        signal: abortSignal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      let accumulatedResponse = '';
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const {value, done} = await reader.read();
        if (done) break;

        // Decode the chunk and add it to our buffer
        buffer += decoder.decode(value, {stream: true});

        // Process complete SSE messages
        const lines = buffer.split('\n');

        // Keep the last incomplete line in the buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6).trim();
            if (!jsonStr) continue;

            try {
              const parsed = JSON.parse(jsonStr);

              if (parsed.type === 'delta_encoding') {
                continue;
              }

              if (parsed.type === 'delta' && parsed.data.v?.message?.content) {
                const content = parsed.data.v.message.content;
                accumulatedResponse += content;

                // Yield each token immediately
                yield {
                  content: [{
                    type: 'text',
                    text: accumulatedResponse
                  }]
                };
              }

              // Handle end of stream
              if (parsed.type === 'delta' && parsed.data.v?.message?.content === '') {
                yield {
                  content: [{
                    type: 'text',
                    text: accumulatedResponse
                  }]
                };
              }
            } catch (e) {
              if (!e.message.includes('Unexpected end of JSON input')) {
                console.error('Error parsing SSE data:', e);
              }
            }
          }
        }
      }

      return {
        content: [{
          type: 'text',
          text: accumulatedResponse
        }]
      };

    } catch (error) {
      console.error('Error in MyModelAdapter:', error);
      throw error;
    }
  }
};

export function MyRuntimeProvider({children}) {
  const [isLoading, setIsLoading] = useState(true);
  const [threads, setThreads] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const runtime = useLocalRuntime(MyModelAdapter, {
    adapters: {
      attachments: attachmentAdapter
    }
  });

  const fetchInitialMessages = async () => {
    try {
      setLoadingMessages(true);
      const idToken = await auth.currentUser.getIdToken(false);
      const threadId = localStorage.getItem('avadaSupportAiAgentThreadId');
      const apiUrl = threadId ? `${process.env.REACT_APP_API_URL}/api/thread/${threadId}/messages` : `${process.env.REACT_APP_API_URL}/api/thread/messages`;
      const response = await fetch(apiUrl, {
        headers: {
          'X-Auth-Token': idToken
        }
      });
      const {data: {messages, conversationId}} = await response.json();
      runtime.reset({initialMessages: messages});
      if (!conversationId) return localStorage.removeItem('avadaSupportAiAgentThreadId');
      localStorage.setItem('avadaSupportAiAgentThreadId', conversationId);

      setIsLoading(false);
    } catch (error) {
      console.error('Failed to fetch initial messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  // Keep original message loading logic
  useEffect(() => {
    fetchInitialMessages();
  }, []);

  // Add separate thread loading logic for testing
  useEffect(() => {
    const fetchThreads = async () => {
      try {
        const idToken = await auth.currentUser.getIdToken(false);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/thread/list`, {
          headers: {
            'X-Auth-Token': idToken
          }
        });
        const {data} = await response.json();
        console.log('Fetched threads:', data.threads); // Log threads for testing
        setThreads(data.threads);
      } catch (error) {
        console.error('Failed to fetch threads:', error);
      }
    };

    fetchThreads();
  }, []);

  const handleChangeThread = (threadId) => {
    localStorage.setItem('avadaSupportAiAgentThreadId', threadId);
    fetchInitialMessages();
  }

  return (
    <ThreadListProvider value={{
      threads,
      setThreads,
      handleChangeThread,
      loadingMessages
    }} >
      <AssistantRuntimeProvider runtime={runtime}>{isLoading ? <Loading /> : children}</AssistantRuntimeProvider>
    </ThreadListProvider>
  )
 ;
}
